import { Button } from "~/react/ui/button"
import type Operation from "~/types/serializers/Operation"
import { useOperation } from "~/react/components/operations/operation-context"
import { capitalize } from "~/lib/text-helpers"
import { CurrencyHelper } from "~/helpers/currency_helper"
import { Avatar, AvatarImage, AvatarFallback } from "~/react/ui/avatar"
import { KeyValue, KeyValueItem, KeyValueTitle, KeyValueDescription } from "~/react/ui/key-value"
import { Tag, TagLabel, TagIcon, TagProps } from "~/react/ui/tag"
import { Icon } from "~/react/ui/icon"
import { leads_path, leads_company_list_path } from "~/lib/routes"
import { humanFileSize } from "~/lib/file-size-helpers"
import moment from "moment"

const sourceTypeLabel: Record<Operation["kind"], (operation: Operation) => string> = {
  "leads/import": (operation) =>
    i18n.t("js.operations.source_type_label.leads/import", {
      count: CurrencyHelper.numberWithDelimiter(operation.total_count),
    }),
  "leads/companies_import": (operation) =>
    i18n.t("js.operations.source_type_label.leads/companies_import", {
      count: CurrencyHelper.numberWithDelimiter(operation.total_count),
    }),
  "leads/find_emails": (operation) =>
    i18n.t("js.operations.source_type_label.leads/find_emails", {
      count: CurrencyHelper.numberWithDelimiter(operation.total_count),
    }),
  "leads/verify": (operation) =>
    i18n.t("js.operations.source_type_label.leads/verify", {
      count: CurrencyHelper.numberWithDelimiter(operation.total_count),
    }),
  "leads/save_leads": (operation) =>
    i18n.t("js.operations.source_type_label.leads/save_leads", {
      count: CurrencyHelper.numberWithDelimiter(operation.total_count),
    }),
  "leads/export": (operation) =>
    i18n.t("js.operations.source_type_label.leads/export", {
      count: CurrencyHelper.numberWithDelimiter(operation.total_count),
    }),
  "leads/companies_export": (operation) =>
    i18n.t("js.operations.source_type_label.leads/companies_export", {
      count: CurrencyHelper.numberWithDelimiter(operation.total_count),
    }),
  reveal_emails: (operation) =>
    i18n.t("js.operations.source_type_label.reveal_emails", {
      source: i18n.t(`js.operations.reveal_emails_source.${operation.metadata.source || "discover"}`),
      count: CurrencyHelper.numberWithDelimiter(operation.total_count),
    }),
  "bulk/verification": (operation) =>
    i18n.t("js.operations.source_type_label.bulk/verification", {
      count: CurrencyHelper.numberWithDelimiter(operation.total_count),
    }),
  "bulk/search": (operation) =>
    i18n.t("js.operations.source_type_label.bulk/search", {
      count: CurrencyHelper.numberWithDelimiter(operation.total_count),
    }),
  "bulk/finder": (operation) =>
    i18n.t("js.operations.source_type_label.bulk/finder", {
      count: CurrencyHelper.numberWithDelimiter(operation.total_count),
    }),
}
const titles: Record<Operation["kind"], (operation: Operation) => string> = {
  "leads/import": (operation) =>
    i18n.t("js.operations.operation_title.leads/import", {
      count: CurrencyHelper.numberWithDelimiter(operation.total_count),
    }),
  "leads/companies_import": (operation) =>
    i18n.t("js.operations.operation_title.leads/companies_import", {
      count: CurrencyHelper.numberWithDelimiter(operation.total_count),
    }),
  "leads/find_emails": (operation) =>
    i18n.t("js.operations.operation_title.leads/find_emails", {
      count: CurrencyHelper.numberWithDelimiter(operation.total_count),
    }),
  "leads/verify": (operation) =>
    i18n.t("js.operations.operation_title.leads/verify", {
      count: CurrencyHelper.numberWithDelimiter(operation.total_count),
    }),
  "leads/save_leads": (operation) =>
    i18n.t("js.operations.operation_title.leads/save_leads", {
      count: CurrencyHelper.numberWithDelimiter(operation.total_count),
    }),
  "leads/export": (operation) =>
    i18n.t("js.operations.operation_title.leads/export", {
      count: CurrencyHelper.numberWithDelimiter(operation.total_count),
    }),
  "leads/companies_export": (operation) =>
    i18n.t("js.operations.operation_title.leads/companies_export", {
      count: CurrencyHelper.numberWithDelimiter(operation.total_count),
    }),
  reveal_emails: (operation) =>
    i18n.t("js.operations.operation_title.reveal_emails", {
      count: CurrencyHelper.numberWithDelimiter(operation.total_count),
    }),
  "bulk/verification": (operation) =>
    i18n.t("js.operations.operation_title.bulk/verification", {
      count: CurrencyHelper.numberWithDelimiter(operation.total_count),
    }),
  "bulk/search": (operation) =>
    i18n.t("js.operations.operation_title.bulk/search", {
      count: CurrencyHelper.numberWithDelimiter(operation.total_count),
    }),
  "bulk/finder": (operation) =>
    i18n.t("js.operations.operation_title.bulk/finder", {
      count: CurrencyHelper.numberWithDelimiter(operation.total_count),
    }),
}
const finishedTitles: Record<Operation["kind"], (operation: Operation) => string> = {
  "leads/import": (operation) =>
    i18n.t("js.operations.finished_operation_title.leads/import", {
      count: CurrencyHelper.numberWithDelimiter(operation.total_count),
    }),
  "leads/companies_import": (operation) =>
    i18n.t("js.operations.finished_operation_title.leads/companies_import", {
      count: CurrencyHelper.numberWithDelimiter(operation.total_count),
    }),
  "leads/find_emails": (operation) =>
    i18n.t("js.operations.finished_operation_title.leads/find_emails", {
      count: CurrencyHelper.numberWithDelimiter(operation.total_count),
    }),
  "leads/verify": (operation) =>
    i18n.t("js.operations.finished_operation_title.leads/verify", {
      count: CurrencyHelper.numberWithDelimiter(operation.total_count),
    }),
  "leads/save_leads": (operation) =>
    i18n.t("js.operations.finished_operation_title.leads/save_leads", {
      count: CurrencyHelper.numberWithDelimiter(operation.total_count),
    }),
  "leads/export": (operation) =>
    i18n.t("js.operations.finished_operation_title.leads/export", {
      count: CurrencyHelper.numberWithDelimiter(operation.total_count),
    }),
  "leads/companies_export": (operation) =>
    i18n.t("js.operations.finished_operation_title.leads/companies_export", {
      count: CurrencyHelper.numberWithDelimiter(operation.total_count),
    }),
  reveal_emails: (operation) =>
    i18n.t("js.operations.finished_operation_title.reveal_emails", {
      count: CurrencyHelper.numberWithDelimiter(operation.total_count),
    }),
  "bulk/verification": (operation) =>
    i18n.t("js.operations.finished_operation_title.bulk/verification", {
      count: CurrencyHelper.numberWithDelimiter(operation.total_count),
    }),
  "bulk/search": (operation) =>
    i18n.t("js.operations.finished_operation_title.bulk/search", {
      count: CurrencyHelper.numberWithDelimiter(operation.total_count),
    }),
  "bulk/finder": (operation) =>
    i18n.t("js.operations.finished_operation_title.bulk/finder", {
      count: CurrencyHelper.numberWithDelimiter(operation.total_count),
    }),
}
const finishedDescriptions: Record<Operation["kind"], (operation: Operation) => string> = {
  "leads/import": (operation) =>
    i18n.t("js.operations.finished_description.leads/import", {
      success_count: CurrencyHelper.numberWithDelimiter(operation.success_count),
      failed_count: CurrencyHelper.numberWithDelimiter(operation.failed_count),
    }),
  "leads/companies_import": (operation) =>
    i18n.t("js.operations.finished_description.leads/companies_import", {
      success_count: CurrencyHelper.numberWithDelimiter(operation.success_count),
      failed_count: CurrencyHelper.numberWithDelimiter(operation.failed_count),
    }),
  "leads/find_emails": (operation) =>
    i18n.t("js.operations.finished_description.leads/find_emails", {
      success_count: CurrencyHelper.numberWithDelimiter(operation.success_count),
      failed_count: CurrencyHelper.numberWithDelimiter(operation.failed_count),
    }),
  "leads/verify": (operation) =>
    i18n.t("js.operations.finished_description.leads/verify", {
      success_count: CurrencyHelper.numberWithDelimiter(operation.success_count),
      failed_count: CurrencyHelper.numberWithDelimiter(operation.failed_count),
    }),
  "leads/save_leads": (operation) =>
    i18n.t("js.operations.finished_description.leads/save_leads", {
      success_count: CurrencyHelper.numberWithDelimiter(operation.success_count),
      failed_count: CurrencyHelper.numberWithDelimiter(operation.failed_count),
    }),
  "leads/export": (operation) =>
    i18n.t("js.operations.finished_description.leads/export", {
      filename: operation.associated_model_metadata?.filename,
      file_size: humanFileSize(operation.associated_model_metadata?.file_size),
    }),
  "leads/companies_export": (operation) =>
    i18n.t("js.operations.finished_description.leads/companies_export", {
      filename: operation.associated_model_metadata?.filename,
      file_size: humanFileSize(operation.associated_model_metadata?.file_size),
    }),
  reveal_emails: (operation) =>
    i18n.t("js.operations.finished_description.reveal_emails", {
      success_count: CurrencyHelper.numberWithDelimiter(operation.success_count),
      failed_count: CurrencyHelper.numberWithDelimiter(operation.failed_count),
    }),
  "bulk/verification": (operation) =>
    i18n.t("js.operations.finished_description.bulk/verification", {
      success_count: CurrencyHelper.numberWithDelimiter(operation.success_count),
      failed_count: CurrencyHelper.numberWithDelimiter(operation.failed_count),
    }),
  "bulk/search": (operation) =>
    i18n.t("js.operations.finished_description.bulk/search", {
      success_count: CurrencyHelper.numberWithDelimiter(operation.success_count),
      failed_count: CurrencyHelper.numberWithDelimiter(operation.failed_count),
    }),
  "bulk/finder": (operation) =>
    i18n.t("js.operations.finished_description.bulk/finder", {
      success_count: CurrencyHelper.numberWithDelimiter(operation.success_count),
      failed_count: CurrencyHelper.numberWithDelimiter(operation.failed_count),
    }),
}

const OperationsCardHeader = ({ children }: { children?: React.ReactNode }) => {
  const { operation, expanded, toggleExpanded } = useOperation()

  const statusIcon = {
    failed: "fas fa-circle-xmark red",
    finished: "fas fa-circle-check green",
    queued: "far fa-circle-dashed text-hint",
    canceled: "far fa-ban text-hint",
    started: "far fa-circle-dashed text-hint",
  }[operation.status]

  const sourceLabel = sourceTypeLabel[operation.kind](operation)
  const isFinished = operation.status === "finished" || operation.status === "failed" || operation.status === "canceled"
  const titleLabel = isFinished ? finishedTitles[operation.kind](operation) : titles[operation.kind](operation)
  const isInProgress = operation.status === "started"

  return (
    <header className="operation-card__header" onClick={toggleExpanded} aria-expanded={expanded}>
      <div className="operation-card__infos">
        <div className="operation-card__meta">{sourceLabel}</div>

        <div className="operation-card__heading">
          <div className="operation-card__icon">
            {isInProgress ? (
              <div className="operation-card-progress-icon">
                <svg className="operation-card-progress-icon__quarter" width="12" height="12" viewBox="0 0 12 12">
                  <path d="M6 6 L6 0 A6 6 0 0 1 12 6 Z" fill="currentColor" />
                </svg>
              </div>
            ) : (
              <span className={statusIcon} aria-hidden="true"></span>
            )}
          </div>
          <h3 className="operation-card__title">{titleLabel}</h3>
        </div>

        <div className="operation-card__description">
          {operation.status === "started" && `${Math.round(operation.percentage_done * 100) / 100}%`}
          {operation.status === "finished" && finishedDescriptions[operation.kind](operation)}
          {operation.associated_model_metadata?.error_file_url && (
            <>
              &nbsp;(
              <a href={operation.associated_model_metadata.error_file_url}>
                {i18n.t("js.operations.actions.download_as_csv")}
              </a>
              )
            </>
          )}
        </div>
      </div>

      {children}
    </header>
  )
}

const OperationsCardActions = ({ children }: { children?: React.ReactNode }) => {
  const { expanded, toggleExpanded } = useOperation()

  return (
    <div className="operation-card__actions" onClick={(e) => e.stopPropagation()}>
      {children}

      <Button
        className="operation-card__toggle"
        size="xs"
        variant="ghost"
        iconOnly={true}
        aria-label="Toggle details"
        aria-expanded={expanded}
        onClick={toggleExpanded}
      >
        {expanded ? <span className="far fa-angle-up"></span> : <span className="far fa-angle-down"></span>}
      </Button>
    </div>
  )
}

const OperationsCardDetails = ({ children }: { children?: React.ReactNode }) => {
  const { operation, expanded } = useOperation()
  const capitalizedStatus = capitalize(operation.status)
  const statusTagVariant: Record<Operation["status"], TagProps["variant"]> = {
    finished: "info",
    canceled: "",
    failed: "danger",
    queued: "info",
    started: "info",
  }[operation.status]

  return (
    <div className={`operation-card-details ${expanded ? "" : "d-none"}`} id="operation-card-details">
      <KeyValue inline={true} grid={true}>
        <KeyValueItem>
          <KeyValueTitle>Status</KeyValueTitle>
          <KeyValueDescription>
            <Tag variant={statusTagVariant}>{capitalizedStatus}</Tag>
          </KeyValueDescription>
        </KeyValueItem>

        {operation.finished_at === null && operation.canceled_at === null && operation.frontend_credit_estimate > 0 && (
          <KeyValueItem>
            <KeyValueTitle>{i18n.t("js.operations.card.cost_estimation")}</KeyValueTitle>
            <KeyValueDescription>
              <Tag>
                <TagIcon>
                  <Icon
                    icon="coin"
                    size="sm"
                    className="fa-rotate-by"
                    style={{ "--fa-rotate-angle": "-45deg" } as React.CSSProperties}
                  />
                </TagIcon>
                <TagLabel>
                  {i18n.t("js.operations.card.credits", {
                    count: operation.frontend_credit_estimate,
                  })}
                </TagLabel>
              </Tag>
            </KeyValueDescription>
          </KeyValueItem>
        )}
        {(operation.finished_at || operation.canceled_at) && operation.frontend_credits_used > 0 && (
          <KeyValueItem>
            <KeyValueTitle>{i18n.t("js.operations.card.credits_used")}</KeyValueTitle>
            <KeyValueDescription>
              <Tag>
                <TagIcon>
                  <Icon
                    icon="coin"
                    size="sm"
                    className="fa-rotate-by"
                    style={{ "--fa-rotate-angle": "-45deg" } as React.CSSProperties}
                  />
                </TagIcon>
                <TagLabel>
                  {i18n.t("js.operations.card.credits", {
                    count: operation.frontend_credits_used,
                  })}
                </TagLabel>
              </Tag>
            </KeyValueDescription>
          </KeyValueItem>
        )}

        {operation.started_at && (
          <KeyValueItem>
            <KeyValueTitle>{i18n.t("js.operations.card.started_at")}</KeyValueTitle>
            <KeyValueDescription>{moment(operation.started_at).format("DD/MM/YYYY · HH:mm:ss")}</KeyValueDescription>
          </KeyValueItem>
        )}

        {operation.finished_at && (
          <KeyValueItem>
            <KeyValueTitle>{i18n.t("js.operations.card.finished_at")}</KeyValueTitle>
            <KeyValueDescription>{moment(operation.finished_at).format("DD/MM/YYYY · HH:mm:ss")}</KeyValueDescription>
          </KeyValueItem>
        )}

        {operation.kind === "leads/import" && (
          <KeyValueItem>
            <KeyValueTitle>{i18n.t("js.operations.card.destination_list")}</KeyValueTitle>
            <KeyValueDescription className="grey">
              {operation.associated_model_metadata?.id ? (
                <a
                  href={leads_path({
                    leads_list_id: operation.associated_model_metadata?.id,
                    leads_import_id: operation.associated_model_metadata?.leads_import_id,
                  })}
                >
                  {operation.associated_model_metadata?.name}
                </a>
              ) : (
                <a
                  href={leads_path({
                    leads_import_id: operation.associated_model_metadata?.leads_import_id,
                  })}
                >
                  {i18n.t("js.operations.card.all_people")}
                </a>
              )}
            </KeyValueDescription>
          </KeyValueItem>
        )}
        {operation.metadata.company_list_id && operation.associated_model_metadata?.id && (
          <KeyValueItem>
            <KeyValueTitle>{i18n.t("js.operations.card.destination_list")}</KeyValueTitle>
            <KeyValueDescription className="grey">
              <a
                href={leads_company_list_path(operation.associated_model_metadata.id, {
                  companies_import_id: operation.associated_model_metadata?.companies_import_id,
                })}
              >
                {operation.associated_model_metadata?.name}
              </a>
            </KeyValueDescription>
          </KeyValueItem>
        )}
      </KeyValue>

      {children}
    </div>
  )
}

const OperationsCardDetailsActions = ({ children }: { children?: React.ReactNode }) => {
  return <div className="operation-card-details__actions">{children}</div>
}

const OperationsCard = ({ status, read, children }: { status: string; read: boolean; children: React.ReactNode }) => {
  return (
    <article className={`operation-card operation-card--${status} operation-card--${read ? "read" : "unread"}`}>
      {children}
    </article>
  )
}

export {
  OperationsCard,
  OperationsCardHeader,
  OperationsCardActions,
  OperationsCardDetails,
  OperationsCardDetailsActions,
}
